import { useEffect, useState } from "react";

import {
    TextField,
    Button,
    MenuItem,
    Snackbar,
    Alert,
    InputAdornment,
    IconButton
} from "@mui/material";

import {
    getUsers,
    addUser,
    updateUser,
    deleteUser
} from "../services/userService";

import { getEmployees } from "../services/employeeService";
import { getBranches } from "../services/branchService";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import ActionButtons from "../components/common/ActionButtons";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { getCurrentUser } from "../utils/currentUser";
import usePagination from "../hooks/usePagination";
import PaginationControls from "../components/common/PaginationControls";

function Users() {

    const [users, setUsers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [branches, setBranches] = useState([]);

    const [search, setSearch] = useState("");

    const [showInactive, setShowInactive] = useState(false);

    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [showPassword, setShowPassword] = useState(false);
    const [sortField, setSortField] = useState("");
    const [sortDirection, setSortDirection] = useState("asc");
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState({
        id: 0,
        username: "",
        password: "",
        role: "User",
        empId: "",
        branchId: "",
        allBranches: "N",
        branchManager: "N",
        status: "Active"
    });

    const loggedInUser = getCurrentUser();

    const role =
        loggedInUser?.role?.toUpperCase() || "";

    const canEdit =
        role === "ADMIN" ||
        role === "MANAGER";

    const canDelete =
        role === "ADMIN";

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success"
    });

    // LOAD DATA
    const loadData = async () => {

        const userRes = await getUsers();
        setUsers(userRes.data || []);

        const empRes = await getEmployees();
        setEmployees(empRes.data || []);

        const branchRes = await getBranches();
        setBranches(branchRes.data || []);
    };

    useEffect(() => {
        loadData();
    }, []);

    // HANDLE CHANGE
    const handleChange = (e) => {

        const { name, value, checked, type } = e.target;
        // ALL BRANCHES
        if (name === "allBranches") {

            setForm(prev => ({
                ...prev,
                allBranches: checked ? "Y" : "N"
            }));

            return;
        }

        // BRANCH MANAGER
        if (name === "branchManager") {

            setForm(prev => ({
                ...prev,
                branchManager: checked ? "Y" : "N"
            }));

            return;
        }

        // EMPLOYEE CHANGE
        if (name === "empId") {

            const selectedEmp = employees.find(
                x => x.id === Number(value)
            );

            setForm(prev => ({
                ...prev,
                empId: value,
                branchId: selectedEmp?.branchId || ""
            }));

            return;
        }
        if (name === "role") {

            // ADMIN
            if (value === "Admin") {

                setForm(prev => ({
                    ...prev,
                    role: value,
                    allBranches: "N",
                    branchManager: "N"
                }));

                return;
            }

            // MANAGER
            if (value === "Manager") {

                setForm(prev => ({
                    ...prev,
                    role: value,
                    allBranches: "N",
                    branchManager: "N"
                }));

                return;
            }

            // USER
            if (value === "User") {

                setForm(prev => ({
                    ...prev,
                    role: value,
                    allBranches: "N",
                    branchManager: "N"
                }));

                return;
            }
        }

        setForm(prev => ({
            ...prev,
            [name]: value
        }));
       
    };

    // SAVE
    const handleSave = async () => {

        // DUPLICATE USERNAME CHECK
        const duplicateUser = users.find(u =>

            u.username.toLowerCase().trim() ===
            form.username.toLowerCase().trim()

            &&

            u.id !== form.id
        );

        if (duplicateUser) {

            setSnackbar({
                open: true,
                message: "Username already exists",
                severity: "error"
            });

            return;
        }

        try {

            const payload = {
                username: form.username,
                password: form.password,
                role: form.role,
                empId: Number(form.empId),
                branchId: Number(form.branchId),
                allBranches: form.allBranches,
                branchManager: form.branchManager,
                status: form.status
            };

            if (isEdit) {

                await updateUser(form.id, payload);

                setSnackbar({
                    open: true,
                    message: "Updated Successfully",
                    severity: "success"
                });

            } else {

                await addUser(payload);

                setSnackbar({
                    open: true,
                    message: "Saved Successfully",
                    severity: "success"
                });
            }

            resetForm();
            loadData();

        } catch (err) {

            console.log(err);

            setSnackbar({
                open: true,
                message: "Error while saving",
                severity: "error"
            });
        }
    };

    // EDIT
    const handleEdit = (row) => {

        setForm({
            id: row.id,
            username: row.username,
            password: row.password,
            role: row.role,
            empId: row.empId,
            branchId: row.branchId,
            allBranches: row.allBranches,
            branchManager: row.branchManager,
            status: row.status
        });

        setIsEdit(true);
    };

    // DELETE
    const handleDelete = async (row) => {

        if (row?.status === "Inactive")
            return;

        if (!window.confirm("Deactivate this user?"))
            return;

        try {

            const payload = {
                username: row.username,
                password: row.password,
                role: row.role,
                empId: row.empId,
                branchId: row.branchId,
                allBranches: row.allBranches,
                branchManager: row.branchManager,
                status: "Inactive"
            };

            await updateUser(row.id, payload);

            setSnackbar({
                open: true,
                message: "User deactivated",
                severity: "success"
            });

            loadData();

        } catch (err) {

            console.error(err);
        }
    };

    const handleRestore = async (row) => {

        try {

            const payload = {
                username: row.username,
                password: row.password,
                role: row.role,
                empId: row.empId,
                branchId: row.branchId,
                allBranches: row.allBranches,
                branchManager: row.branchManager,
                status: "Active"
            };

            await updateUser(row.id, payload);

            setSnackbar({
                open: true,
                message: "User restored",
                severity: "success"
            });

            loadData();

        } catch (err) {

            console.error(err);
        }
    };

    const handlePermanentDelete = async (row) => {

        if (!window.confirm(
            `Permanently delete "${row.username}" ?`
        ))
            return;

        try {

            await deleteUser(row.id);

            setSnackbar({
                open: true,
                message: "User permanently deleted",
                severity: "error"
            });

            loadData();

        } catch (err) {

            console.error(err);
        }
    };

    // RESET
    const resetForm = () => {

        setForm({
            id: 0,
            username: "",
            password: "",
            role: "User",
            empId: "",
            branchId: "",
            allBranches: "N",
            branchManager: "N"

        });

        setIsEdit(false);
    };
    const handleSort = (field) => {

        if (sortField === field) {

            setSortDirection(prev =>
                prev === "asc" ? "desc" : "asc"
            );

        } else {

            setSortField(field);
            setSortDirection("asc");
        }
    };
    const highlightText = (text) => {

        if (!search)
            return text;

        const regex = new RegExp(`(${search})`, "gi");

        return String(text).split(regex).map((part, index) =>

            part.toLowerCase() === search.toLowerCase()

                ? (
                    <span
                        key={index}
                        style={{
                            backgroundColor: "yellow",
                            fontWeight: "bold"
                        }}
                    >
                        {part}
                    </span>
                )

                : part
        );
    };
    const filteredUsers = (users || [])

        .filter(u => {

            // hide inactive rows
            if (!showInactive && u.status === "Inactive")
                return false;

            const searchText = search.toLowerCase();

            return (
                (u.username || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                (u.employeeName || "")
                    .toLowerCase()
                    .includes(searchText)

                ||

                (u.role || "")
                    .toLowerCase()
                    .includes(searchText)
            );
        })

        .sort((a, b) => {

            if (!sortField)
                return 0;

            let valA = a[sortField] || "";
            let valB = b[sortField] || "";

            if (typeof valA === "string")
                valA = valA.toLowerCase();

            if (typeof valB === "string")
                valB = valB.toLowerCase();

            if (valA < valB)
                return sortDirection === "asc" ? -1 : 1;

            if (valA > valB)
                return sortDirection === "asc" ? 1 : -1;

            return 0;
        });

    const {
        page,
        setPage,
        totalPages,
        paginatedData
    } = usePagination(filteredUsers, rowsPerPage);

    useEffect(() => {
        setPage(0);
    }, [search, showInactive, rowsPerPage]);

    const exportToExcel = () => {

        const data = filteredUsers.map(u => ({
            Username: u.username,
            Employee: u.employeeName,
            Branch: u.branchName,
            Role: u.role,
            AllBranches: u.allBranches,
            BranchManager: u.branchManager
        }));

        const ws = XLSX.utils.json_to_sheet(data);

        const wb = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(wb, ws, "Users");

        const buffer = XLSX.write(wb, {
            bookType: "xlsx",
            type: "array"
        });

        saveAs(
            new Blob([buffer]),
            "Users.xlsx"
        );
    };

    return (

        <div style={styles.container}>

            {/* HEADER */}
            <div style={styles.header}>
                <h2>Users Master</h2>
            </div>

            {/* FORM */}
            <div style={styles.card}>

                <div style={styles.formContainer}>

                    {/* ROW-1 */}
                    <div style={styles.row}>

                        <TextField
                            label="Username"
                            name="username"
                            value={form.username}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={styles.inputStyle}
                        />

                        <TextField
                            label="Password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            value={form.password}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={styles.inputStyle}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">

                                        <IconButton
                                            edge="end"
                                            onClick={() =>
                                                setShowPassword(!showPassword)
                                            }
                                            size="small"
                                        >
                                            {showPassword
                                                ? <VisibilityOff fontSize="small" />
                                                : <Visibility fontSize="small" />
                                            }
                                        </IconButton>

                                    </InputAdornment>
                                )
                            }}
                        />

                    </div>

                    {/* ROW-2 */}
                    <div style={styles.row}>

                        <TextField
                            select
                            label="Employee"
                            name="empId"
                            value={form.empId}
                            onChange={handleChange}
                            fullWidth
                            size="small"
                            sx={styles.inputStyle}
                        >
                            {/*{employees.map(emp => (*/}

                            {/*    <MenuItem*/}
                            {/*        key={emp.id}*/}
                            {/*        value={emp.id}*/}
                            {/*        sx={{*/}
                            {/*            fontFamily: "Verdana",*/}
                            {/*            fontSize: "12px"*/}
                            {/*        }}*/}
                            {/*    >*/}
                            {/*        {emp.empName}*/}
                            {/*    </MenuItem>*/}

                            {/*))}*/}
                            {[...employees]

                                .sort((a, b) =>
                                    a.empName.localeCompare(b.empName)
                                )

                                .map(emp => {

                                const branchName = branches.find(
                                    b => b.id === emp.branchId
                                )?.branchName || "";

                                return (

                                    <MenuItem
                                        key={emp.id}
                                        value={emp.id}
                                        sx={{
                                            fontFamily: "Verdana",
                                            fontSize: "12px"
                                        }}
                                    >
                                        <div style={{
                                            display: "flex",
                                            width: "100%",
                                            alignItems: "center"
                                        }}>

                                            {/* EMPLOYEE */}
                                            <span style={{
                                                width: "160px",
                                                overflow: "hidden",
                                                whiteSpace: "nowrap",
                                                textOverflow: "ellipsis"
                                            }}>
                                                {emp.empName}
                                            </span>

                                            {/* BRANCH */}
                                            <span style={{
                                                width: "140px"
                                            }}>
                                                {"-> "} {branchName}
                                            </span>

                                            {/* MOBILE */}
                                            <span>
                                                {"-> "} {emp.mobileNo}
                                            </span>

                                        </div>
                                    </MenuItem>

                                );
                            })}
                        </TextField>

                        <TextField
                            label="Branch"
                            value={
                                branches.find(
                                    b => b.id === form.branchId
                                )?.branchName || ""
                            }
                            fullWidth
                            size="small"
                            sx={styles.inputStyle}
                            InputProps={{
                                readOnly: true
                            }}
                        />

                    </div>

                    {/* ROW-3 */}
                    <div style={styles.row}>

                        {/* LEFT SIDE */}
                        <div style={{ flex: 1 }}>

                            <TextField
                                select
                                label="Role"
                                name="role"
                                value={form.role}
                                onChange={handleChange}
                                fullWidth
                                size="small"
                                sx={styles.inputStyle}
                            >
                                <MenuItem
                                    value="Admin"
                                    sx={{
                                        fontFamily: "Verdana",
                                        fontSize: "12px"
                                    }}
                                >
                                    Admin
                                </MenuItem>

                                <MenuItem
                                    value="Manager"
                                    sx={{
                                        fontFamily: "Verdana",
                                        fontSize: "12px"
                                    }}
                                >
                                    Manager
                                </MenuItem>

                                <MenuItem
                                    value="User"
                                    sx={{
                                        fontFamily: "Verdana",
                                        fontSize: "12px"
                                    }}
                                >
                                    User
                                </MenuItem>

                            </TextField>

                        </div>

                        {/* RIGHT SIDE */}
                        <div style={{
                            flex: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: "30px",
                            paddingLeft: "0px",
                            fontFamily: "Verdana",
                            fontSize: "12px"
                        }}>

                            <label style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <input
                                    type="checkbox"
                                    name="allBranches"
                                    checked={form.allBranches === "Y"}
                                    onChange={handleChange}
                                    disabled={
                                        form.role === "Manager" ||
                                        form.role === "User"
                                    }
                                />

                                All Branches
                            </label>

                            <label style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px"
                            }}>
                                <input
                                    type="checkbox"
                                    name="branchManager"
                                    checked={form.branchManager === "Y"}
                                    onChange={handleChange}
                                    disabled={
                                        form.role === "Admin" ||
                                        form.role === "User"
                                    }
                                />

                                Branch Manager
                            </label>

                        </div>

                    </div>

                </div>

                {/* BUTTONS */}
                <div style={styles.buttonRow}>

                    <Button
                        variant="contained"
                        onClick={handleSave}
                        sx={{
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "12px",
                            fontFamily: "Verdana",
                            padding: "0 12px",
                            textTransform: "none !important"
                        }}
                    >
                        {isEdit ? "Update" : "Save"}
                    </Button>

                    {isEdit && (

                        <Button
                            variant="contained"
                            onClick={resetForm}
                            sx={{
                                height: "28px",
                                minHeight: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                padding: "0 12px",
                                marginLeft: "10px",
                                textTransform: "none !important",
                                background: "#2f3542"
                            }}
                        >
                            Cancel
                        </Button>

                    )}

                </div>

            </div>

            {/* GRID */}
            <div style={styles.card}>
                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    gap: "10px",
                    width: "100%"
                }}>

                    {/* SHOW INACTIVE */}
                    <label style={{
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px"
                    }}>
                        <input
                            type="checkbox"
                            checked={showInactive}
                            onChange={(e) =>
                                setShowInactive(e.target.checked)
                            }
                        />

                        Show Inactive
                    </label>

                    {/* SEARCH */}
                    <TextField
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        size="small"
                        sx={{
                            width: "220px",

                            "& .MuiOutlinedInput-root": {
                                height: "28px",
                                fontFamily: "Verdana"
                            },

                            "& .MuiOutlinedInput-input": {
                                padding: "4px 10px",
                                fontSize: "12px",
                                fontFamily: "Verdana"
                            }
                        }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),

                            endAdornment: search && (
                                <InputAdornment position="end">

                                    <IconButton
                                        size="small"
                                        onClick={() => setSearch("")}
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>

                                </InputAdornment>
                            )
                        }}
                    />

                    {/* EXPORT */}
                    <Button
                        variant="contained"
                        onClick={exportToExcel}
                        sx={{
                            height: "28px",
                            minHeight: "28px",
                            fontSize: "12px",
                            fontFamily: "Verdana",
                            padding: "0 12px",
                            background: "#3742fa"
                        }}
                    >
                        EXPORT
                    </Button>

                    {/* ROWS */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "5px"
                    }}>

                        <span style={{ fontSize: "12px" }}>
                            Rows
                        </span>

                        <select
                            value={rowsPerPage}
                            onChange={(e) =>
                                setRowsPerPage(parseInt(e.target.value))
                            }
                            style={{
                                height: "28px",
                                fontSize: "12px",
                                fontFamily: "Verdana",
                                border: "1px solid #ccc",
                                borderRadius: "4px",
                                padding: "0 5px"
                            }}
                        >
                            {[5, 10, 20, 30, 50].map(n => (
                                <option
                                    key={n}
                                    value={n}
                                >
                                    {n}
                                </option>
                            ))}
                        </select>

                    </div>

                </div>

                <div style={{
                    ...styles.tableWrapper,
                    marginTop: "10px"
                }}>

                    <table style={styles.table}>

                        <thead style={styles.tableHeader}>

                            <tr>

                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("username")}
                                >
                                    Username{" "}
                                    {sortField === "username"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>

                                <th
                                    style={{
                                        ...styles.th,
                                        cursor: "pointer",
                                        width: "220px"
                                    }}
                                    onClick={() => handleSort("employeeName")}
                                >
                                    Employee{" "}
                                    {sortField === "employeeName"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>
                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("employeeMobile")}
                                >
                                    Mobile{" "}
                                    {sortField === "employeeMobile"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>
                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("branchName")}
                                >
                                    Branch{" "}
                                    {sortField === "branchName"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>
                                <th
                                    style={{ ...styles.th, cursor: "pointer" }}
                                    onClick={() => handleSort("role")}
                                >
                                    Role{" "}
                                    {sortField === "role"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>
                                <th
                                    style={{
                                        ...styles.th,
                                        cursor: "pointer",
                                        width: "90px",
                                        textAlign: "center"
                                    }}
                                    onClick={() => handleSort("allBranches")}
                                >
                                    All Branches{" "}
                                    {sortField === "allBranches"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>
                                <th
                                    style={{
                                        ...styles.th,
                                        cursor: "pointer",
                                        width: "110px",
                                        textAlign: "center"
                                    }}
                                    onClick={() => handleSort("branchManager")}
                                >
                                    Branch Manager{" "}
                                    {sortField === "branchManager"
                                        ? (sortDirection === "asc" ? "↑" : "↓")
                                        : "↕"}
                                </th>
                                <th style={styles.th}>Actions</th>

                            </tr>

                        </thead>

                        <tbody>

                            {paginatedData.map(row => (

                                <tr key={row.id}>

                                    <td style={styles.td}>
                                        {highlightText(row.username)}
                                    </td>

                                    <td style={styles.td}>
                                        {highlightText(row.employeeName)}
                                    </td>

                                    <td style={styles.td}>
                                        {highlightText(row.employeeMobile)}
                                    </td>

                                    <td style={styles.td}>
                                        {highlightText(row.branchName)}
                                    </td>

                                    <td style={styles.td}>
                                        {highlightText(row.role)}
                                    </td>

                                    <td style={{
                                        ...styles.td,
                                        textAlign: "center"
                                    }}>
                                        {highlightText(row.allBranches)}
                                    </td>

                                    <td style={{
                                        ...styles.td,
                                        textAlign: "center"
                                    }}>
                                        {highlightText(row.branchManager)}
                                    </td>

                                    <td style={{
                                        ...styles.td,
                                        textAlign: "center"
                                    }}>

                                        <ActionButtons
                                            row={row}
                                            status={row.status}
                                            onEdit={handleEdit}
                                            onDelete={handleDelete}
                                            onPermanentDelete={handlePermanentDelete}
                                            onRestore={() => handleRestore(row)}
                                            canEdit={canEdit}
                                            canDelete={canDelete}
                                        />

                                    </td>

                                </tr>

                            ))}

                        </tbody>

                    </table>

                </div>
                {/* PAGINATION */}
                <div style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                    marginTop: "10px",
                    gap: "10px",
                    fontSize: "12px",
                    fontFamily: "Verdana"
                }}>

                    <PaginationControls
                        page={page}
                        setPage={setPage}
                        totalPages={totalPages}
                    />

                </div>
            </div>

            {/* SNACKBAR */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() =>
                    setSnackbar(prev => ({
                        ...prev,
                        open: false
                    }))
                }
            >
                <Alert severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </div>
    );
}

const styles = {

    container: {
        padding: "5px",
        background: "#f1f2f6",
        fontFamily: "Verdana",
        fontSize: "12px"
    },

    header: {
        background: "#2f3542",
        color: "#fff",
        padding: "6px",
        borderRadius: "6px",
        marginBottom: "10px"
    },

    card: {
        background: "#fff",
        padding: "15px",
        borderRadius: "15px",
        marginBottom: "10px",
        boxShadow: "0 1px 4px rgba(0,0,0,0.1)"
    },

    formContainer: {
        display: "flex",
        flexDirection: "column",
        gap: "10px"
    },

    row: {
        display: "flex",
        gap: "10px"
    },

    tableWrapper: {
        width: "100%",
        overflowX: "auto"
    },

    table: {
        width: "100%",
        borderCollapse: "collapse"
    },

    tableHeader: {
        background: "#57606f",
        color: "#fff"
    },

    th: {
        border: "1px solid #ccc",
        padding: "8px",
        backgroundColor: "#5f6b7a",
        color: "#fff",
        fontWeight: "bold",
        textAlign: "left"
    },

    td: {
        border: "1px solid #ddd",
        padding: "8px"
    },

    buttonRow: {
        marginTop: "10px"
    },

    editBtn: {
        background: "#2ed573",
        border: "none",
        padding: "6px",
        borderRadius: "4px",
        cursor: "pointer",
        marginRight: "5px",
        color: "#fff",
        fontSize: "12px",
        fontFamily: "Verdana"
    },

    deleteBtn: {
        background: "#ff4757",
        border: "none",
        padding: "6px",
        borderRadius: "4px",
        cursor: "pointer",
        color: "#fff",
        fontSize: "12px",
        fontFamily: "Verdana"
    },

    inputStyle: {

        "& .MuiInputBase-root": {
            height: "32px"
        },

        "& .MuiInputBase-input": {
            fontFamily: "Verdana",
            fontSize: "12px",
            padding: "8.5px 12px"
        },

        "& .MuiInputLabel-root": {
            fontFamily: "Verdana",
            fontSize: "12px"
        },

        "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            fontFamily: "Verdana",
            fontSize: "12px",
            padding: "8.5px 12px !important"
        }
    }
};

export default Users;